import React from 'react';
import styled, { css } from 'styled-components';
import { PX_TO_REM, Size, SIZES } from '../../fundamentals/sizes/sizes';
import { FONT_SIZES, FONT_WEIGHTS, FontSize, useDefaultFontCss } from '../../fundamentals/typography/typography';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { Icon } from '../Icon/Icon';
import { MaterialIcon } from '../Icon/icons';
import { useActionCss, useFocusCss } from '../../fundamentals/actions';
import { IGutterAsMarginProps, useGutterAsMarginCss } from '../../fundamentals/spaces/space';
import { BASE_STATE_KEYS, BaseStateKey, IColoursCollection, MapColourStates, StateColourType } from '../../fundamentals/colours/colourInterfaces';
import { BASE_STATUS_COLOURS, EXTRA_BRAND_COLOURS, EXTRA_PALETTE_COLOURS, MAIN_BRAND_COLOURS } from '../../fundamentals/colours/variants';
import { useFilterColourCss } from '../../fundamentals/colours/filter';
import { useCenteredFlexCss } from '../../fundamentals/display';
import { getStringHash } from '../../common/utils';
import { Span } from '../Texts/Texts';
import { PERSONAL_PRONOUNS } from '../../common/constants';
import { BaseDiv } from '../../common/BaseElements';
import { CoreDark } from '../../fundamentals/colours/eoraColours';

export type AvatarSize =
  | typeof SIZES.MICRO
  | typeof SIZES.XXX_SMALL
  | typeof SIZES.XX_SMALL
  | typeof SIZES.X_SMALL
  | typeof SIZES.SMALL
  | typeof SIZES.MEDIUM
  | typeof SIZES.LARGE
  | typeof SIZES.X_LARGE
  | typeof SIZES.MACRO;

export const AVATAR_VARIANTS = { ...EXTRA_BRAND_COLOURS, ...BASE_STATUS_COLOURS, ...EXTRA_PALETTE_COLOURS } as const;
type AvatarVariant = (typeof AVATAR_VARIANTS)[keyof typeof AVATAR_VARIANTS];
export interface IAvatarProps extends IGutterAsMarginProps, React.HTMLAttributes<HTMLDivElement> {
  size?: AvatarSize;
  img?: string;
  icon?: MaterialIcon;
  name?: string;
  instrument?: string; // use this to trigger instrument fontSize resizing
  variant?: AvatarVariant;
  inverted?: boolean;
  coloured?: boolean;
  border?: boolean;
}

const avatarIconSize: Record<AvatarSize, Size> = {
  [SIZES.MICRO]: SIZES.X_MICRO,
  [SIZES.XXX_SMALL]: SIZES.MICRO,
  [SIZES.XX_SMALL]: SIZES.XXX_SMALL,
  [SIZES.X_SMALL]: SIZES.XX_SMALL,
  [SIZES.SMALL]: SIZES.X_SMALL,
  [SIZES.MEDIUM]: SIZES.SMALL,
  [SIZES.LARGE]: SIZES.MEDIUM,
  [SIZES.X_LARGE]: SIZES.LARGE,
  [SIZES.MACRO]: SIZES.XXX_LARGE
};

const avatarFontSize: Record<AvatarSize, FontSize> = {
  [SIZES.MICRO]: FONT_SIZES.TEXT_NANO,
  [SIZES.XXX_SMALL]: FONT_SIZES.TEXT_NANO,
  [SIZES.XX_SMALL]: FONT_SIZES.TEXT_NANO,
  [SIZES.X_SMALL]: FONT_SIZES.TEXT_NANO,
  [SIZES.SMALL]: FONT_SIZES.TEXT_SMALL,
  [SIZES.MEDIUM]: FONT_SIZES.TEXT_DEFAULT,
  [SIZES.LARGE]: FONT_SIZES.TEXT_LARGE,
  [SIZES.X_LARGE]: FONT_SIZES.HEADING_5,
  [SIZES.MACRO]: FONT_SIZES.DISPLAY_SMALL
};

const instrumentCodeAvatarFontSize: Record<AvatarSize, FontSize> = {
  [SIZES.MICRO]: FONT_SIZES.TEXT_NANO,
  [SIZES.XXX_SMALL]: FONT_SIZES.TEXT_NANO,
  [SIZES.XX_SMALL]: FONT_SIZES.TEXT_NANO,
  [SIZES.X_SMALL]: FONT_SIZES.TEXT_NANO,
  [SIZES.SMALL]: FONT_SIZES.TEXT_NANO,
  [SIZES.MEDIUM]: FONT_SIZES.TEXT_NANO,
  [SIZES.LARGE]: FONT_SIZES.TEXT_NANO,
  [SIZES.X_LARGE]: FONT_SIZES.TEXT_MICRO,
  [SIZES.MACRO]: FONT_SIZES.TEXT_DEFAULT
};

const getFontSizeForInstrumentCode = (size: AvatarSize, code: string) => {
  if (size === SIZES.MACRO && code.length < 5) {
    return FONT_SIZES.HEADING_5;
  }
  if (size === SIZES.MACRO && code.length === 5) {
    return FONT_SIZES.HEADING_6;
  }
  if (size === SIZES.MEDIUM && code.length >= 4) {
    return FONT_SIZES.TEXT_FEMTO; // SBCI-1806: Requirement for 4-char instrument codes not to be truncated. See margin calc below in StyledComponent "Text"
  }

  return instrumentCodeAvatarFontSize[size];
};

const formColour = (colour: StateColourType, backgroundOverride?: string) => ({
  background: backgroundOverride ?? colour.colour,
  text: colour.text,
  hover: {
    background: colour.hover
  },
  active: {
    background: colour.active
  }
});

const setImageEffectStyle = (effectKey: BaseStateKey) => css<Pick<IAvatarProps, `img`>>`
  ${({ img }) => css`
    ${effectKey === BASE_STATE_KEYS.HOVER &&
    `background: linear-gradient(0deg, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.1)), url(${img}) center center / cover no-repeat;`}

    ${effectKey === BASE_STATE_KEYS.ACTIVE &&
    `background: linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(${img}) center center / cover no-repeat;`}
  `}
`;

const setDefaultEffectStyle = (effectKey: BaseStateKey) => css<Pick<IAvatarProps, `variant`>>`
  ${({ variant, theme }) => css`
    ${effectKey === BASE_STATE_KEYS.HOVER && `background: ${theme.avatarColours[variant!].hover!.background};`}

    ${effectKey === BASE_STATE_KEYS.ACTIVE && `background: ${theme.avatarColours[variant!].hover!.background};`}
  `}
`;

export const setAvatarEffectStyle = (effectKey: BaseStateKey) => css<Pick<IAvatarProps, `img`>>`
  ${({ img }) => css`
    ${img ? setImageEffectStyle(effectKey) : setDefaultEffectStyle(effectKey)}
  `}
`;

const formEoraSecondaryColour = (eoraCore: CoreDark) => ({
  background: eoraCore.BACKGROUND_COLOR.SECONDARY_BASE.COLOR_BACKGROUND_SECONDARYBASE,
  text: eoraCore.TEXT_COLOR.COLOR_TEXT_DEFAULT,
  hover: {
    background: eoraCore.BACKGROUND_COLOR.SECONDARY_BASE.COLOR_BACKGROUND_SECONDARYBASE
  },
  active: {
    background: eoraCore.BACKGROUND_COLOR.SECONDARY_BASE.COLOR_BACKGROUND_SECONDARYBASE
  }
});

type AvatarColours = { avatarColours: Record<AvatarVariant, MapColourStates> & Pick<MapColourStates, 'border'> };
export const avatarColours = ({ primary, accent, secondaryInverted, status, extra, palette, eoraCoreColours }: IColoursCollection): AvatarColours => ({
  avatarColours: {
    border: `${PX_TO_REM[1]} solid ${eoraCoreColours.BORDER_COLOR.COLOR_BORDER_LIGHT}`, // used for non-texts avatars
    [EXTRA_BRAND_COLOURS.PRIMARY]: formColour(primary),
    [EXTRA_BRAND_COLOURS.ACCENT]: formColour(accent),
    [EXTRA_BRAND_COLOURS.SECONDARY]: formEoraSecondaryColour(eoraCoreColours),
    [EXTRA_BRAND_COLOURS.SECONDARY_INVERTED]: formColour(secondaryInverted.light),
    [BASE_STATUS_COLOURS.INFO]: formColour(status.info, palette.info),
    [BASE_STATUS_COLOURS.SUCCESS]: formColour(status.success, palette.success),
    [BASE_STATUS_COLOURS.ERROR]: formColour(status.error, palette.error),
    [BASE_STATUS_COLOURS.WARNING]: formColour(status.warning, palette.warning),
    [EXTRA_PALETTE_COLOURS.MINT]: formColour(extra.mint, palette.mint),
    [EXTRA_PALETTE_COLOURS.ROSE]: formColour(extra.rose, palette.rose),
    [EXTRA_PALETTE_COLOURS.SKY]: formColour(extra.sky, palette.sky),
    [EXTRA_BRAND_COLOURS.CLOSEBUTTONONPRIMARY]: formColour(primary)
  }
});

const Container = styled(BaseDiv)<IAvatarProps>`
  ${useCenteredFlexCss};
  ${useGutterAsMarginCss()};

  border-radius: ${({ theme, size }) => theme.sizes[size!]};
  height: ${({ theme, size }) => theme.sizes[size!]};
  min-height: ${({ theme, size }) => theme.sizes[size!]};
  width: ${({ theme, size }) => theme.sizes[size!]};
  min-width: ${({ theme, size }) => theme.sizes[size!]};

  ${Icon} {
    ${({ theme, variant }) => useFilterColourCss(theme.avatarColours[variant!].text)};
  }

  ${({ img, theme, variant, border }) =>
    img
      ? css`
          background: url(${img}) ${theme.avatarColours[variant!].background} center center / cover no-repeat;
          ${border &&
          css`
            border: ${theme.avatarColours.border};
          `}
        `
      : css`
          background: ${theme.avatarColours[variant!].background};
        `};

  ${({ onClick }) =>
    onClick &&
    css`
      &:hover {
        ${setAvatarEffectStyle(BASE_STATE_KEYS.HOVER)};
      }
      &:active {
        ${setAvatarEffectStyle(BASE_STATE_KEYS.ACTIVE)};
      }
    `};

  ${useFocusCss()};
  ${useActionCss};
`;

const Text = styled(Span)<IAvatarProps>`
  ${useDefaultFontCss};

  color: ${({ theme, variant }) => theme.avatarColours[variant!].text};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ instrument, size, theme }) => {
    const MARGIN_RATIO = 12;
    return instrument && size === SIZES.MEDIUM
      ? css`
          margin: 0 calc(${instrument.length < 4 ? PX_TO_REM[56] : PX_TO_REM[44]} / ${MARGIN_RATIO});
        `
      : css`
          margin: 0 calc(${theme.sizes[size!]} / ${MARGIN_RATIO});
        `;
  }};
`;

export const generateAvatarText = (name?: string) => {
  const possibleSalutation = name?.split(` `)?.[0] ?? '';
  const simpleName = PERSONAL_PRONOUNS.includes(possibleSalutation.toLowerCase()) ? name?.replace(possibleSalutation, '') : name;

  return simpleName
    ?.trim()
    ?.match(/(?=\b)[\dA-Za-z]/g)
    ?.join(``)
    ?.slice(0, 2)
    .toUpperCase();
};

export const generateInstrumentText = (instrument?: string, size: AvatarSize = SIZES.MEDIUM) => {
  return instrument && (size === SIZES.XX_SMALL || size === SIZES.X_SMALL ? instrument.charAt(0) : instrument.split(`:`)?.[0]);
};

const computeVariant = ({ variant, inverted, coloured, name, instrument, icon }: Omit<IAvatarProps, 'size' | 'image'>) => {
  if (inverted) {
    return EXTRA_BRAND_COLOURS.SECONDARY_INVERTED;
  }

  if (variant) {
    return variant;
  }

  // Removed secondary
  const COLOURED_VARIANTS = { ...BASE_STATUS_COLOURS, ...EXTRA_PALETTE_COLOURS, ...MAIN_BRAND_COLOURS };

  if (coloured && (name || instrument || icon)) {
    const allVariants = Object.values(COLOURED_VARIANTS);
    const hash = getStringHash((name ?? instrument ?? icon)!);
    const length = allVariants.length - 1; // - 1 for `border` key
    return allVariants[hash % length];
  }

  return EXTRA_BRAND_COLOURS.SECONDARY;
};

export const Avatar: React.FC<IAvatarProps> = ({ size = SIZES.MEDIUM, onClick, tabIndex, border = true, ...rest }) => {
  const { generateTestId } = useTestId(rest, `Avatar`);
  const { name, instrument, img, icon } = rest;
  const text = generateAvatarText(name);

  const computedVariant = computeVariant(rest);
  const trimmedInstrumentCode = generateInstrumentText(instrument, size);

  const handleOnclick = (e: React.MouseEvent<HTMLDivElement>) => {
    onClick?.(e);
    e.currentTarget.blur();
  };

  return (
    <Container
      onClick={onClick && handleOnclick}
      tabIndex={tabIndex ?? (onClick ? 0 : undefined)}
      {...generateTestId()}
      {...rest}
      variant={computedVariant}
      size={size}
      border={border}
    >
      {!img && !icon && (
        <Text
          fontWeightCss={instrument ? FONT_WEIGHTS.SEMI_BOLD : FONT_WEIGHTS.MEDIUM}
          name={name}
          variant={computedVariant}
          size={size}
          instrument={trimmedInstrumentCode}
          textAlign='center'
          textSize={instrument ? getFontSizeForInstrumentCode(size, instrument) : avatarFontSize[size]}
          {...generateTestId(`text`)}
        >
          {(!trimmedInstrumentCode && text) || trimmedInstrumentCode || ``}
        </Text>
      )}
      {icon && <Icon {...generateTestId(`icon`)} name={icon} size={avatarIconSize[size]} />}
    </Container>
  );
};
